import React from "react";
import { TextField, TextFieldProps } from "@mui/material";

interface DuplicateDetectionTextFieldProps extends Omit<TextFieldProps, "onChange" | "value" | "error" | "helperText"> {
  value: string;
  onChange: (value: string) => void;
  existingItems: Array<{ name: string; id?: string; [key: string]: any }>; // Allow additional properties
  currentItemId?: string; // ID of the item being edited (to exclude from duplicate check)
  errorMessageFormat?: (itemName: string, itemType: string, item?: { name: string; id?: string; [key: string]: any }) => string;
  itemType?: string; // Type of item (e.g., "group", "account") for error messages
  onErrorChange?: (error: string) => void; // Callback to notify parent of error state
}

/**
 * A TextField component that automatically detects duplicate names in real-time.
 * Shows error messages when a duplicate is detected.
 */
function DuplicateDetectionTextField({
  value,
  onChange,
  existingItems,
  currentItemId,
  errorMessageFormat,
  itemType = "item",
  onErrorChange,
  ...textFieldProps
}: DuplicateDetectionTextFieldProps): React.ReactElement {
  const [error, setError] = React.useState<string>("");

  // Default error message format
  const defaultErrorMessageFormat = (itemName: string, type: string, item?: { name: string; id?: string; [key: string]: any }): string => {
    return `A ${type} named "${itemName}" already exists. Please choose a different name.`;
  };

  const getErrorMessage = errorMessageFormat || defaultErrorMessageFormat;

  // Check for duplicates when value changes
  React.useEffect(() => {
    if (value.trim()) {
      const trimmedValue = value.trim();
      const duplicateItem = existingItems.find(
        (item) =>
          item.name.toLowerCase() === trimmedValue.toLowerCase() &&
          item.id !== currentItemId
      );

      if (duplicateItem) {
        const errorMsg = getErrorMessage(duplicateItem.name, itemType, duplicateItem);
        setError(errorMsg);
        if (onErrorChange) {
          onErrorChange(errorMsg);
        }
      } else {
        setError("");
        if (onErrorChange) {
          onErrorChange("");
        }
      }
    } else {
      setError("");
      if (onErrorChange) {
        onErrorChange("");
      }
    }
  }, [value, existingItems, currentItemId, itemType, getErrorMessage, onErrorChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    onChange(e.target.value);
  };

  return (
    <TextField
      {...textFieldProps}
      value={value}
      onChange={handleChange}
      error={!!error}
      helperText={error}
      sx={{
        "& .MuiFormHelperText-root": {
          fontSize: "0.75rem",
        },
        ...textFieldProps.sx,
      }}
    />
  );
}

export default DuplicateDetectionTextField;
